import dayjs from 'dayjs';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

import {CaptchaStore} from '@yourcoach/captcha';
import {i18n} from '@yourcoach/i18n';

import type {AccountApiModel} from '../../../api/account';
import type {SendCodeResult} from '../../../api/auth';
import type {CoreUserAuthSignupParams} from '../../../api/types/core/methods';
import type {Role, UserExpandedCommon} from '../../../api/user';
import type {Nullable} from '../../../utils/utility-types';
import type {Config, ConfirmCodeError, SentTo} from './types';

import {AccountStore, accountStore} from '../../../api/account';
import {AuthStore, authStore} from '../../../api/auth';
import {currentUserStore} from '../../../api/user';
import {accountAdd} from '../../../services/entity-action/account/account.add';
import {datetimeObjToISOString} from '../../../utils/datetime';
import {envManager} from '../../../utils/env';
import {getUserAgent} from '../../../utils/getUserAgent';
import {logger} from '../../../utils/logger';
import {
  type Destructible,
  ReactionDisposeManager,
  type WithReactionDispose,
} from '../../../utils/store';

/**
 * The store contains logic of applying the confirmation code
 * during login/signup or adding as additional account
 */
export class ApplyCodeStore implements Destructible, WithReactionDispose {
  static CODE_SIZE = 6;
  static DEFAULT_USER_NAME = 'New User';
  code: Config['code'];
  readonly disposer = new ReactionDisposeManager();
  externalId: Nullable<string> = null;
  inProgress = false;
  isTestClient: Config['isTestClient'];
  linkTo: Config['linkTo'];
  partnerId: Nullable<string> = null;
  sendCodeResult: Config['sendCodeResult'];
  sentTo: Config['sentTo'];
  private captcha: CaptchaStore;
  private isAuthFlow: Config['isAuthFlow'];
  private onApplyCodeError: Config['onApplyCodeError'];
  private onApplyCodeSuccess: Config['onApplyCodeSuccess'];
  private onBeforeApplyCode: Config['onBeforeApplyCode'];
  private onBeforeMergeAccounts: Config['onBeforeMergeAccounts'];
  private onBeforeSendCode: Config['onBeforeSendCode'];
  private onLogin: Config['onLogin'];
  private onMergeAccountsError: Config['onMergeAccountsError'];
  private onMergeAccountsSuccess: Config['onMergeAccountsSuccess'];
  private onSendCodeError: Config['onSendCodeError'];
  private onSendCodeSuccess: Config['onSendCodeSuccess'];
  private onSignup: Config['onSignup'];
  private platform: Config['platform'];
  private roles: Config['roles'];
  private useOnboarding: Config['useOnboarding'];

  constructor(config: Config) {
    this.code = config.code;
    this.sendCodeResult = config.sendCodeResult;
    this.isAuthFlow = config.isAuthFlow;
    this.linkTo = config.linkTo;
    this.sentTo = config.sentTo;
    this.useOnboarding = config.useOnboarding;
    this.isTestClient = config.isTestClient;
    this.roles = config.roles;
    this.platform = config.platform;
    this.onBeforeSendCode = config.onBeforeSendCode;
    this.onSendCodeSuccess = config.onSendCodeSuccess;
    this.onSendCodeError = config.onSendCodeError;
    this.onBeforeApplyCode = config.onBeforeApplyCode;
    this.onApplyCodeSuccess = config.onApplyCodeSuccess;
    this.onApplyCodeError = config.onApplyCodeError;
    this.onBeforeMergeAccounts = config.onBeforeMergeAccounts;
    this.onMergeAccountsSuccess = config.onMergeAccountsSuccess;
    this.onMergeAccountsError = config.onMergeAccountsError;
    this.onSignup = config.onSignup;
    this.onLogin = config.onLogin;

    this.captcha = new CaptchaStore(config.captchaKey, {
      shouldBeMocked: config.mockCaptcha,
    });

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );

    this.setupReactions();
  }

  destruct() {
    this.disposer.dispose();
  }

  get resendTimeout() {
    if (!this.sendCodeResult) {
      return 0;
    }

    const sent = dayjs(datetimeObjToISOString(this.sendCodeResult.sent));
    const expires = dayjs(datetimeObjToISOString(this.sendCodeResult.expires));

    return expires.diff(sent, 'second') - 1;
  }

  async mergeAccounts() {
    try {
      this.onBeforeMergeAccounts?.();

      runInAction(() => {
        this.inProgress = true;
      });

      const recaptchaToken = await this.captcha.getToken(
        AccountStore.MERGE_CAPTCHA_ACTION,
      );

      await accountStore.merge(
        {
          ...this.sentTo,
          code: this.code!,
          platform: this.platform,
        },
        recaptchaToken,
      );

      runInAction(() => {
        this.inProgress = false;
      });

      this.onMergeAccountsSuccess?.();
    } catch (error) {
      runInAction(() => {
        this.inProgress = false;
      });

      logger.error(error);

      const {message, title} = this.handleError(error);

      this.onMergeAccountsError?.({
        ...error,
        message,
        title,
      });
    }
  }

  setCode(code: string) {
    this.code = code;
  }

  setExternalId(externalId: string) {
    this.externalId = externalId;
  }

  setPartnerId(partnerId: string) {
    this.partnerId = partnerId;
  }

  setSendCodeResult(result: SendCodeResult) {
    this.sendCodeResult = result;
  }

  setSentTo(sentTo: SentTo) {
    this.sentTo = sentTo;
  }

  private async applyCode() {
    const authMethod = this.sendCodeResult!.user_exists ? 'login' : 'signup';

    let account: AccountApiModel | undefined;

    this.onBeforeApplyCode?.();

    runInAction(() => {
      this.inProgress = true;
    });

    try {
      if (this.isAuthFlow) {
        const recaptchaToken = await this.captcha.getToken(
          authMethod === 'login'
            ? AuthStore.LOGIN_CAPTCHA_ACTION
            : AuthStore.SIGNUP_CAPTCHA_ACTION,
        );

        const params: {
          agent: string;
          anyRole?: boolean;
          code: string;
          email?: string;
          externalId?: string;
          isTestClient?: boolean;
          name: string;
          partnerId?: Nullable<string>;
          phone?: string;
          platform?: CoreUserAuthSignupParams['platform'];
          pushToken?: null | string;
          roles?: Role[];
        } = {
          ...this.sentTo,
          agent: getUserAgent(),
          code: this.code!,
          name: ApplyCodeStore.DEFAULT_USER_NAME,
          platform: this.platform,
          roles: this.roles,
        };

        if (envManager.isCore && authMethod === 'signup') {
          if (this.partnerId) {
            params.partnerId = this.partnerId;
          }

          if (this.isTestClient) {
            params.isTestClient = true;
          }

          if (this.externalId) {
            params.externalId = this.externalId;
          }
        }

        const result = await authStore[authMethod](params, recaptchaToken);

        const method = this.sentTo?.email ? 'email' : 'phone';

        const isOnboardingFinished = this.useOnboarding
          ? !!(
              currentUserStore.user &&
              (
                currentUserStore.user.metadata.onboarding || {
                  is_finished: authMethod !== 'signup',
                }
              ).is_finished
            )
          : true;

        let invitesCount = 0;

        if (!isOnboardingFinished) {
          const onboardingMetadataForUpdate: UserExpandedCommon['metadata'] = {
            onboarding: {
              account_type: null,
              coupon_reservation: null,
              is_finished: false,
            },
          };

          if (authMethod === 'signup') {
            invitesCount = (result as {invites_count: number}).invites_count;

            onboardingMetadataForUpdate.onboarding!.invites_count =
              invitesCount;
          }

          if (
            currentUserStore.user &&
            currentUserStore.user.metadata.onboarding &&
            currentUserStore.user.metadata.onboarding.invites_count !==
              undefined
          ) {
            invitesCount =
              currentUserStore.user.metadata.onboarding.invites_count;
          }

          const metadata = {
            ...currentUserStore.user!.metadata,
            ...onboardingMetadataForUpdate,
          };

          await currentUserStore.update({
            b2b: {
              metadata,
            },
            core: {
              metadata,
            },
          });
        }

        if (authMethod === 'signup' || !isOnboardingFinished) {
          await this.onSignup?.(method, invitesCount);
        } else {
          await this.onLogin?.(method);
        }

        runInAction(() => {
          this.inProgress = false;
        });
      } else {
        const createdAccount = await accountAdd({
          ...this.sentTo,
          code: this.code!,
        });

        runInAction(() => {
          this.inProgress = false;
        });

        account = createdAccount;
      }

      await this.onApplyCodeSuccess?.(account);
    } catch (error) {
      runInAction(() => {
        this.inProgress = false;
      });

      logger.error(error);

      const {message, title} = this.handleError(error);

      this.onApplyCodeError?.({
        ...error,
        message,
        title,
      });
    }
  }

  private handleError(error: any): ConfirmCodeError {
    let {message} = error;
    let title = i18n.t('shared:message.error');

    const isAccountExist =
      !this.isAuthFlow && error.code === 'error.resource.invalid_context';

    if (error.code === 'error.resource.user.invalid_code') {
      this.code = '';
      message = i18n.t('shared:confirmation_code.wrong_code_error');
    } else if (error.code === 'error.resource.user.too_many_attempts') {
      this.code = '';
      message = i18n.t('shared:confirmation_code.too_many_attempts_error');
    } else if (error.code === 'error.resource.user.expired_code') {
      this.code = '';
      this.sendCode();
      message = i18n.t('shared:confirmation_code.expired_code_error');
    } else if (error.code === 'error.resource.user.blocked_account') {
      message = i18n.t('shared:confirmation_code.blocked_account_error');
    } else if (isAccountExist) {
      error.code = 'merge_accounts_error';
      title = i18n.t('shared:confirmation_code.merge_accounts_error.title');
      message =
        /**
         * @i18n_parser
         * t('shared:confirmation_code.merge_accounts_error.message.email')
         * t('shared:confirmation_code.merge_accounts_error.message.phone')
         */
        i18n.t(
          `shared:confirmation_code.merge_accounts_error.message.${
            this.sentTo!.email ? 'email' : 'phone'
          }`,
        );
    } else if (error.code === 'login.not_supported_roles') {
      this.code = '';
    } else {
      title = i18n.t('shared:message.error_fix');
    }

    return {
      ...error,
      message,
      title,
    };
  }

  private async sendCode() {
    this.onBeforeSendCode?.();

    runInAction(() => {
      this.inProgress = true;
    });

    try {
      const recaptchaToken = await this.captcha.getToken(
        AuthStore.SEND_CODE_CAPTCHA_ACTION,
      );

      const sendCodeResult = await authStore.sendCode(
        {...this.sentTo!, linkTo: this.linkTo},
        recaptchaToken,
      );

      runInAction(() => {
        this.inProgress = false;

        this.sendCodeResult = sendCodeResult;
      });

      this.onSendCodeSuccess?.(sendCodeResult);

      return sendCodeResult;
    } catch (error) {
      runInAction(() => {
        this.inProgress = false;
      });

      this.onSendCodeError?.({
        ...error,
        title: i18n.t('shared:confirmation_code.send_code_error'),
      });
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'code',
      reaction(
        () => this.code,
        code => {
          if (code?.length === ApplyCodeStore.CODE_SIZE) {
            this.applyCode();
          }
        },
      ),
    );
  }
}
